import React from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import "dayjs/locale/en";
import {
  ItemWrapper,
  ContentWrapper,
  Error,
  InputWrapper,
  InputContainer,
  ButtonsWrapper,
} from "../../styles";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { CNYProps, FormInputs } from "../../../../../../types/walletTypes";
import { CNYIDTypes } from "../../../../const";
import { savedCNYData } from "../../../../redux/formDataSlice";
import { Input } from "../../../../../../components/common/Input";
import CountrySelect from "../../../../../../components/common/CountrySelect";
import { Button } from "../../../../../../components/common/Button";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import CustomDatePicker from "../../../../../../components/common/CustomDateRangePicker/CustomDatePicker";
import dayjs from "dayjs";
import { isUnderage } from "../../../../../Onboarding/utils/helperFunctions";

const validateSenderBirthDate = (value: unknown) => {
  if (!value) return "Birth date is required";

  const birthday = dayjs(value as string);
  if (!birthday.isValid()) return "Please enter a valid date";
  if (isUnderage(birthday)) return "Sender must be 18 years or older";

  return true;
};

const Sender = ({ handleNext, handlePrevious }: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);

  const {senderBirthDate, ...defaultValues} = getSavedData || {};

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues,
  });

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    dispatch(savedCNYData({...data,
        senderBirthDate: dayjs(data?.senderBirthDate).format("YYYY-MM-DD"),
    }));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="senderFirstName"
            control={control}
            rules={{ required: "First name is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="First name"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.senderFirstName}
                labelColor="#FFFFFF"
              />
            )}
          />
          {<Error>{errors?.senderFirstName?.message}</Error>}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="senderLastName"
            control={control}
            rules={{ required: "Last name is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Last name"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors.senderLastName}
                labelColor="#FFFFFF"
              />
            )}
          />
          {<Error>{errors?.senderLastName?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>

        <InputWrapper>
            <Controller
                name="senderBirthDate"
                control={control}
                rules={{
                  required: "Birth date is required",
                  validate: validateSenderBirthDate,
                }}
                render={({ field }) => (
                    <CustomDatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Sender birth date"
                        disabledDate={isUnderage}
                        lightBorder
                    />
                )}
            />
            <Error>{errors?.senderBirthDate?.message}</Error>
        </InputWrapper>

      <ItemWrapper>
        <InputContainer>
          <InputWrapper>
            <Controller
              name="senderIdType"
              control={control}
              rules={{ required: "ID type is required" }}
              render={({ field }) => (
                <CustomSelect
                  value={field.value}
                  options={CNYIDTypes.filter(
                    (data) =>
                      ![
                        "UNIFIED_SOCIAL_CREDIT_ID",
                        "HONGKONG_MACAO_TAIWAN_PERMIT", "BUSINESS_REGISTRATION_CERT", "OTHER"
                      ].includes(data.value),
                  )}
                  onChange={field.onChange}
                  $width="100%"
                  placeholder="ID Type"
                  lightBorder
                />
              )}
            />
            {<Error>{errors?.senderIdType?.message}</Error>}
          </InputWrapper>
        </InputContainer>

        <InputWrapper>
          <Controller
            name="senderIdNumber"
            control={control}
            rules={{ required: "ID number is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="ID Number"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.senderIdNumber}
              />
            )}
          />
          {<Error>{errors?.senderIdNumber?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>

      <InputWrapper>
        <Controller
          name="senderAddress.streetAddress"
          control={control}
          rules={{ required: "Street address is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Street address"
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors?.senderAddress?.streetAddress}
            />
          )}
        />
        {<Error>{errors?.senderAddress?.streetAddress?.message}</Error>}
      </InputWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="senderAddress.city"
            control={control}
            rules={{ required: "City is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="City"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.senderAddress?.city}
              />
            )}
          />
          {<Error>{errors?.senderAddress?.city?.message}</Error>}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="senderAddress.state"
            control={control}
            rules={{ required: "State is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="State"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.senderAddress?.state}
              />
            )}
          />
          {<Error>{errors?.senderAddress?.state?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>

      <ItemWrapper>
        <InputContainer>
          <InputWrapper>
            <Controller
              name="senderAddress.countryCode"
              control={control}
              rules={{ required: "Country is required" }}
              render={({ field }) => (
                <InputWrapper>
                  <CountrySelect
                    value={field.value}
                    onChange={field.onChange}
                    allCountries={true}
                    placeholder="Country"
                    lightBorder
                  />
                </InputWrapper>
              )}
            />
            {<Error>{errors?.senderAddress?.countryCode?.message}</Error>}
          </InputWrapper>
        </InputContainer>

        <InputWrapper>
          <Controller
            name="senderAddress.postcode"
            control={control}
            rules={{ required: "Postcode is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Postcode"
                value={field.value}
                onChange={field.onChange}
                hasError={!!errors?.senderAddress?.postcode}
              />
            )}
          />
          {<Error>{errors?.senderAddress?.postcode?.message}</Error>}
        </InputWrapper>
      </ItemWrapper>


      <ButtonsWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default Sender;
